import { Button, ButtonGroup, Modal } from "@datapay-by-datacom/design-system";
import { OrgKey, OrgKeyCategory } from "../../generated/core-api";
import { useUserOrgs } from "../../org/hooks";
import { useAuthenticated } from "../../authn";
import { AsyncResult } from "../../shared/components/AsyncResult";
import s from "./ThreadKnowledgeSelector.module.css";
import { useState, useCallback, useEffect } from "react";
import { UIThread } from "../../generated/core-api";
import { OrgKeySelector } from "../../org/components/OrgSelector";


interface KnowledgeSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: OrgKey[]) => void;
  onCancel: () => void;
  value: OrgKey[];
}

/**
 * Modal dialog for selecting knowledge sources (organizations)
 */
export function KnowledgeSelectorModal({
  isOpen,
  onClose,
  onSave,
  onCancel,
  value: initialValue,
}: KnowledgeSelectorModalProps) {
  const { userId } = useAuthenticated();
  const userOrgsQuery = useUserOrgs(userId);
  const [selectedOrgs, setSelectedOrgs] = useState<OrgKey[]>(initialValue);

  // Reset selected orgs when modal opens with new initial value
  useEffect(() => {
    setSelectedOrgs(initialValue);
  }, [initialValue]);

  const handleSave = () => {
    onSave(selectedOrgs);
    onClose();
  };

  const handleCancel = () => {
    // Reset to initial value when cancelled
    setSelectedOrgs(initialValue);
    onCancel();
    onClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <div className={s.modalContent}>
        <div className={s.modalHeader}>
          <h2>Select Knowledge Sources</h2>
        </div>

        <div className={s.modalBody}>
          <AsyncResult query={userOrgsQuery}>
            {(userOrgs) => (
              <OrgKeySelector
                value={selectedOrgs}
                onChange={setSelectedOrgs}
                userOrgs={userOrgs}
              />
            )}
          </AsyncResult>
        </div>

        <div className={s.modalFooter}>
          <ButtonGroup>
            <Button variant="secondary" onPress={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleSave}>
              Save
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Modal>
  );
}

interface KnowledgeSelectorButtonProps {
  onClick: () => void;
  selectedCount: number;
  totalCount: number;
  isDisabled?: boolean;
}

/**
 * Button that shows the count of selected knowledge sources
 */
export function KnowledgeSelectorButton({
  onClick,
  selectedCount,
  totalCount,
  isDisabled = false,
}: KnowledgeSelectorButtonProps) {
  return (
    <Button
      variant="secondary"
      onPress={onClick}
      isDisabled={isDisabled}
      aria-label={`Using Knowledge Sources: ${selectedCount} of ${totalCount}`}
    >
      Using Knowledge Sources: {selectedCount} of {totalCount}
    </Button>
  );
}

interface ThreadKnowledgeSelectorProps {
  thread: UIThread;
  setThreadKnowledge: (knowledgeFromOrgs: OrgKey[]) => Promise<void>;
  userOrgs: { id: string; category: OrgKeyCategory; name: string }[];
  isDisabled?: boolean;
}

/**
 * Combined component that manages thread knowledge selection
 */
export function ThreadKnowledgeSelector({
  thread,
  setThreadKnowledge,
  userOrgs,
  isDisabled = false,
}: ThreadKnowledgeSelectorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const handleSave = useCallback(
    async (selectedOrgs: OrgKey[]) => {
      try {
        await setThreadKnowledge(selectedOrgs);
      } catch (error) {
        console.error("Failed to update thread knowledge:", error);
      }
    },
    [setThreadKnowledge]
  );

  const handleCancel = useCallback(() => {
    // No action needed, modal will just close
  }, []);

  return (
    <>
      <KnowledgeSelectorButton
        onClick={toggleModal}
        selectedCount={thread.knowledge_from_orgs.length}
        totalCount={userOrgs.length}
        isDisabled={isDisabled}
      />

      <KnowledgeSelectorModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        onSave={handleSave}
        onCancel={handleCancel}
        value={thread.knowledge_from_orgs}
      />
    </>
  );
}
